import { NonconformityStatus } from '@qualicloud/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Children, useMemo, type ReactNode } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  IconAlertTriangle,
  IconCalendar,
  IconCheckCircle,
  IconClipboardCheck,
  IconInbox,
  LoadingRegion,
  Skeleton,
  cn,
  focusRing,
} from '@/design-system';
import { capaApi, circularsApi, meetingsApi, nonconformitiesApi, queryKeys } from '@/lib/api';
import { formatDateOnly, formatDateTime } from '@/lib/format/datetime';
import { useOrganizationTimeZone } from '@/providers/auth-provider';
import { linkTarget } from '@/routes/link-target';
import { CapaDueBadge, CriticalityBadge, NonconformityStatusBadge } from './sgq-badges';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PAINEL DO USUÁRIO — o que do SGQ depende de MIM
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Cinco filas, todas com a MESMA pergunta por trás: o que trava por minha
 * causa? O painel do legado respondia a outra pergunta — quantos registros
 * existem por balde — e dois dos quatro baldes de NC eram literalmente zero
 * (BUG-IND-03). Contador não é pendência.
 *
 * Cada bloco usa os filtros de escopo que o servidor já implementa
 * (`scope=RECEIVED`, `scope=ASSIGNED_TO_ME`, `onlyAwaitingMyVerification`,
 * `scope=PARTICIPANT`, `onlyPendingMyAcknowledgement`), então a decisão de "é
 * meu?" é do backend, com a ACL aplicada — nunca um filtro do cliente sobre uma
 * lista maior.
 *
 * ACESSIBILIDADE: cada fila é uma `<section>` com heading próprio e o contador
 * no rótulo, de modo que a navegação por headings responde "quantas pendências
 * de cada tipo" sem entrar em nenhuma lista.
 */

const PREVIEW_SIZE = 5;

/** NCs que ainda exigem trabalho. Encerrada e improcedente não são pendência. */
const OPEN_NC_STATUSES = [
  NonconformityStatus.OPEN,
  NonconformityStatus.UNDER_EVALUATION,
  NonconformityStatus.ACCEPTED,
  NonconformityStatus.IN_TREATMENT,
  NonconformityStatus.PENDING_EFFECTIVENESS,
];

export function SgqUserPanel() {
  const timeZone = useOrganizationTimeZone();

  /** Janela da semana corrente, no fuso da organização. */
  const week = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7);
    return { start, end };
  }, []);

  const myNcFilter = {
    page: 1,
    pageSize: PREVIEW_SIZE,
    scope: 'RECEIVED' as const,
    status: OPEN_NC_STATUSES,
    sortBy: 'criticality' as const,
    sortDir: 'desc' as const,
  };
  const myNonconformities = useQuery({
    queryKey: queryKeys.nonconformities.list(myNcFilter),
    queryFn: () => nonconformitiesApi.list(myNcFilter),
  });

  const myActionsFilter = {
    page: 1,
    pageSize: PREVIEW_SIZE,
    scope: 'ASSIGNED_TO_ME' as const,
    sortBy: 'dueDate' as const,
    sortDir: 'asc' as const,
  };
  const myActions = useQuery({
    queryKey: queryKeys.capa.actions(myActionsFilter),
    queryFn: () => capaApi.listActions(myActionsFilter),
  });

  const myVerificationsFilter = {
    page: 1,
    pageSize: PREVIEW_SIZE,
    onlyAwaitingMyVerification: true,
    sortBy: 'dueDate' as const,
    sortDir: 'asc' as const,
  };
  const myVerifications = useQuery({
    queryKey: queryKeys.capa.actions(myVerificationsFilter),
    queryFn: () => capaApi.listActions(myVerificationsFilter),
  });

  const myMeetingsFilter = {
    page: 1,
    pageSize: PREVIEW_SIZE,
    scope: 'PARTICIPANT' as const,
    startsFrom: week.start,
    startsTo: week.end,
    sortBy: 'startsAt' as const,
    sortDir: 'asc' as const,
  };
  const myMeetings = useQuery({
    queryKey: queryKeys.meetings.list(myMeetingsFilter),
    queryFn: () => meetingsApi.list(myMeetingsFilter),
  });

  const myCircularsFilter = {
    page: 1,
    pageSize: PREVIEW_SIZE,
    onlyPendingMyAcknowledgement: true,
    sortBy: 'publishedAt' as const,
    sortDir: 'desc' as const,
  };
  const myCirculars = useQuery({
    queryKey: queryKeys.circulars.list(myCircularsFilter),
    queryFn: () => circularsApi.list(myCircularsFilter),
  });

  return (
    <section aria-labelledby="sgq-panel-heading" className="flex flex-col gap-4">
      <h2 id="sgq-panel-heading" className="text-base font-semibold text-content">
        Sistema da qualidade — o que depende de você
      </h2>

      <div className="grid gap-4 xl:grid-cols-2">
        <PanelBlock
          title="Minhas não-conformidades"
          description="Registros sob sua responsabilidade que ainda exigem tratamento, do mais crítico para o menos."
          icon={<IconAlertTriangle size={20} />}
          total={myNonconformities.data?.total ?? 0}
          loading={myNonconformities.isPending}
          seeAll={{ to: '/nao-conformidades', search: { scope: 'RECEIVED', page: 1 } }}
          emptyTitle="Nenhuma não-conformidade sob sua responsabilidade"
          emptyDescription="Quando um registro for atribuído a você, ele aparece aqui e também nas suas notificações."
        >
          {(myNonconformities.data?.items ?? []).map((nc) => (
            <PanelRow
              key={nc.id}
              to="/nao-conformidades/$nonconformityId"
              params={{ nonconformityId: nc.id }}
              title={`${nc.code} — ${nc.title}`}
              subtitle={`Fato em ${formatDateOnly(nc.detectedOn)}`}
              trailing={
                <div className="flex shrink-0 items-center gap-2">
                  <CriticalityBadge
                    variant="chip"
                    score={nc.managerCriticalityScore ?? nc.criticalityScore}
                    level={nc.managerCriticalityLevel ?? nc.criticalityLevel}
                  />
                  <NonconformityStatusBadge status={nc.status} />
                </div>
              }
            />
          ))}
        </PanelBlock>

        <PanelBlock
          title="Minhas ações vencendo"
          description="Ações atribuídas a você, ordenadas pelo prazo. O semáforo usa a mesma regra do servidor."
          icon={<IconClipboardCheck size={20} />}
          total={myActions.data?.total ?? 0}
          loading={myActions.isPending}
          seeAll={{ to: '/acoes-corretivas', search: { scope: 'ASSIGNED_TO_ME', page: 1 } }}
          emptyTitle="Nenhuma ação atribuída a você"
          emptyDescription="As solicitações chegam aqui quando alguém atribui uma a você — vindas da análise de causa de uma não-conformidade ou abertas avulsas."
        >
          {(myActions.data?.items ?? []).map((action) => (
            <PanelRow
              key={action.id}
              to="/acoes-corretivas/$actionId"
              params={{ actionId: action.id }}
              title={action.title}
              subtitle={`${action.progressPercent}% concluído · causa: ${action.rootCauseDescription}`}
              trailing={
                <CapaDueBadge
                  variant="chip"
                  state={action.dueState}
                  status={action.status}
                  dueDate={action.plan.when}
                />
              }
            />
          ))}
        </PanelBlock>

        <PanelBlock
          title="Ações aguardando sua verificação"
          description="Ações concluídas que você solicitou. Quem pediu a ação é quem sabe se o problema acabou — e a verificação é ato assinado."
          icon={<IconCheckCircle size={20} />}
          total={myVerifications.data?.total ?? 0}
          loading={myVerifications.isPending}
          seeAll={{
            to: '/acoes-corretivas',
            search: { onlyAwaitingMyVerification: true, page: 1 },
          }}
          emptyTitle="Nenhuma verificação pendente"
          emptyDescription="Quando uma ação que você solicitou for concluída, ela aparece aqui para a verificação de eficácia."
          highlight={(myVerifications.data?.total ?? 0) > 0}
        >
          {(myVerifications.data?.items ?? []).map((action) => (
            <PanelRow
              key={action.id}
              to="/acoes-corretivas/$actionId"
              params={{ actionId: action.id }}
              title={action.title}
              subtitle={`Executada por ${action.assigneeName ?? 'responsável não identificado'}`}
              trailing={
                <Badge tone="warning" srPrefix="Situação:">
                  Aguarda sua verificação
                </Badge>
              }
            />
          ))}
        </PanelBlock>

        <PanelBlock
          title="Reuniões da semana"
          description="Reuniões dos próximos sete dias para as quais você foi convidado."
          icon={<IconCalendar size={20} />}
          total={myMeetings.data?.total ?? 0}
          loading={myMeetings.isPending}
          seeAll={{ to: '/reunioes', search: { scope: 'PARTICIPANT', page: 1 } }}
          emptyTitle="Nenhuma reunião nos próximos sete dias"
          emptyDescription="Convites novos aparecem aqui e na sua caixa de notificações."
        >
          {(myMeetings.data?.items ?? []).map((meeting) => (
            <PanelRow
              key={meeting.id}
              to="/reunioes/$meetingId"
              params={{ meetingId: meeting.id }}
              title={meeting.subject}
              subtitle={`${formatDateTime(meeting.startsAt, timeZone)} · ${meeting.location}`}
              trailing={
                <Badge tone="outline" srPrefix="Não-conformidades vinculadas:">
                  {meeting.nonconformityCount} NC
                </Badge>
              }
            />
          ))}
        </PanelBlock>

        <PanelBlock
          title="Circulares sem ciência"
          description="Comunicados publicados que aguardam a sua confirmação. Abrir a circular não registra ciência: a confirmação é ato deliberado e assinado."
          icon={<IconInbox size={20} />}
          total={myCirculars.data?.total ?? 0}
          loading={myCirculars.isPending}
          seeAll={{
            to: '/circulares',
            search: { onlyPendingMyAcknowledgement: true, page: 1 },
          }}
          emptyTitle="Nenhuma circular pendente"
          emptyDescription="Você está em dia com as circulares que recebeu."
          highlight={(myCirculars.data?.total ?? 0) > 0}
          className="xl:col-span-2"
        >
          {(myCirculars.data?.items ?? []).map((circular) => (
            <PanelRow
              key={circular.id}
              to="/circulares/$circularId"
              params={{ circularId: circular.id }}
              title={`${circular.code} — ${circular.title}`}
              subtitle={
                circular.publishedAt
                  ? `Publicada em ${formatDateTime(circular.publishedAt, timeZone)}`
                  : 'Rascunho'
              }
              trailing={
                <Badge tone="warning" srPrefix="Situação:">
                  Aguarda sua ciência
                </Badge>
              }
            />
          ))}
        </PanelBlock>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function PanelBlock({
  title,
  description,
  icon,
  total,
  loading,
  seeAll,
  emptyTitle,
  emptyDescription,
  highlight = false,
  className,
  children,
}: {
  readonly title: string;
  readonly description: string;
  readonly icon: ReactNode;
  readonly total: number;
  readonly loading: boolean;
  readonly seeAll: { readonly to: string; readonly search: Record<string, unknown> };
  readonly emptyTitle: string;
  readonly emptyDescription: string;
  readonly highlight?: boolean;
  readonly className?: string;
  readonly children: ReactNode;
}) {
  // `Children.toArray` já descarta `null`, `undefined` e booleanos e atribui
  // chaves estáveis — é o que distingue "fila vazia" de "fila com itens".
  const rows = Children.toArray(children);
  const hasRows = rows.length > 0;

  return (
    <Card
      padding="none"
      className={cn('min-w-0', highlight && total > 0 && 'border-l-4 border-l-warning', className)}
    >
      <div className="p-4">
        <CardHeader
          headingLevel={3}
          title={
            <span className="flex items-center gap-2">
              <span aria-hidden="true" className="text-content-subtle">
                {icon}
              </span>
              {title}
              <Badge tone={total > 0 ? 'brand' : 'neutral'} size="sm">
                {total}
              </Badge>
            </span>
          }
          description={description}
          action={
            total > 0 ? (
              <Button asChild variant="secondary" size="sm">
                {/* Alvo vindo de DADO: `linkTarget` é o ponto único onde o
                    projeto converte caminho dinâmico para o `to` tipado do
                    roteador (`routes/link-target.ts`). O filtro de busca segue
                    a mesma conversão. */}
                <Link {...linkTarget(seeAll.to)} search={seeAll.search as never}>
                  Ver todas
                </Link>
              </Button>
            ) : undefined
          }
        />
      </div>

      {loading ? (
        // O `Skeleton` é `aria-hidden` por contrato: sem a região que anuncia,
        // a fila inteira não existe para o leitor de tela enquanto carrega e as
        // linhas aparecem em silêncio. Cada fila anuncia a SUA carga porque as
        // cinco consultas são independentes e terminam em momentos diferentes.
        <LoadingRegion loading label={`Carregando ${title.toLowerCase()}…`}>
          <div className="flex flex-col gap-2 px-4 pb-4">
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton key={index} className="h-12 w-full" />
            ))}
          </div>
        </LoadingRegion>
      ) : !hasRows ? (
        <EmptyState variant="compact" title={emptyTitle} description={emptyDescription} />
      ) : (
        <ul className="border-t border-border">{rows}</ul>
      )}
    </Card>
  );
}

function PanelRow({
  to,
  params,
  title,
  subtitle,
  trailing,
}: {
  readonly to: string;
  readonly params: Record<string, string>;
  readonly title: string;
  readonly subtitle: string;
  readonly trailing: ReactNode;
}) {
  return (
    <li className="border-b border-border-subtle last:border-b-0">
      <Link
        {...linkTarget(to, params)}
        className={cn(
          'flex flex-wrap items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-sunken',
          focusRing,
        )}
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-content">{title}</span>
          <span className="block truncate text-xs text-content-subtle">{subtitle}</span>
        </span>
        {trailing}
      </Link>
    </li>
  );
}
